import React from 'react';
import { numberInCommaFormat, getPartner } from '../../../../../../lib/access.es6';
import ButtonInc from '../../../../../../shared-ui/ButtonInc';
import { getAddedBenefits, getRemovedBenefits } from '../utils/benefitHelpers';

const styles = {
  container: {
    padding: '20px'
  },
  header: {
    marginBottom: '30px'
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '10px'
  },
  description: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '20px'
  },
  changeSection: {
    marginBottom: '30px',
    backgroundColor: '#f9f9f9',
    padding: '20px',
    borderRadius: '8px'
  },
  memberName: {
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '15px',
    color: '#032A37'
  },
  changeType: {
    fontSize: '14px',
    fontWeight: 'bold',
    marginBottom: '10px',
    marginTop: '15px'
  },
  benefitList: {
    listStyle: 'none',
    padding: 0,
    margin: 0
  },
  benefitItem: {
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between'
  },
  benefitName: {
    fontSize: '14px'
  },
  benefitPremium: {
    fontSize: '14px',
    color: '#666'
  },
  premiumSummary: {
    marginTop: '15px',
    padding: '15px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    border: '1px solid #ddd'
  },
  premiumRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '5px 0',
    fontSize: '14px'
  },
  premiumTotal: {
    fontWeight: 'bold',
    fontSize: '16px',
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '2px solid #032A37'
  },
  addedBenefit: {
    color: '#28a745'
  },
  removedBenefit: {
    color: '#dc3545',
    textDecoration: 'line-through'
  },
  noChanges: {
    padding: '20px',
    textAlign: 'center',
    color: '#999',
    fontStyle: 'italic'
  },
  navigationButtons: {
    marginTop: '30px',
    display: 'flex',
    justifyContent: 'space-between'
  }
};

export default function SummaryScreen(props) {
  const { originalMembers, updatedMembers, onBack, onConfirm } = props;
  const currencyCode = getPartner().country.currency_code;

  // Calculate changes for each member
  const memberChanges = updatedMembers.map(updatedMember => {
    const originalMember = originalMembers.find(m => m.uuid === updatedMember.uuid);
    if (!originalMember) return null;

    const originalBenefits = originalMember.benefits || [];
    const newBenefits = updatedMember.benefits || [];

    const added = getAddedBenefits(originalBenefits, newBenefits);
    const removed = getRemovedBenefits(originalBenefits, newBenefits);

    if (added.length === 0 && removed.length === 0) return null;

    return {
      member: updatedMember,
      originalPremium: originalMember.premium_amount_in_cents || 0,
      newPremium: updatedMember.premium_amount_in_cents || 0,
      added,
      removed
    };
  }).filter(change => change !== null);

  // Calculate total premium changes
  const originalTotalPremium = originalMembers.reduce((sum, m) => sum + (m.premium_amount_in_cents || 0), 0);
  const newTotalPremium = updatedMembers.reduce((sum, m) => sum + (m.premium_amount_in_cents || 0), 0);
  const premiumDifference = newTotalPremium - originalTotalPremium;

  if (memberChanges.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>Benefit Changes Summary</div>
        </div>
        <div style={styles.noChanges}>
          No benefit changes were made
        </div>
        <div style={styles.navigationButtons}>
          <ButtonInc onClick={onBack} variant="outlined">
            Back
          </ButtonInc>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.title}>Confirm Benefit Changes</div>
        <div style={styles.description}>
          Please review the benefit changes below before confirming the amendment.
        </div>
      </div>

      {memberChanges.map((change) => (
        <div key={change.member.uuid} style={styles.changeSection}>
          <div style={styles.memberName}>
            {change.member.first_name} {change.member.last_name} ({change.member.relationship})
          </div>

          {change.added.length > 0 && (
            <div>
              <div style={{ ...styles.changeType, ...styles.addedBenefit }}>
                ✓ Benefits Added:
              </div>
              <ul style={styles.benefitList}>
                {change.added.map((benefit, idx) => {
                  const benefitName = benefit.name || benefit.type || 'Unknown Benefit';
                  const benefitPremium = benefit.premium_amount ||
                    (benefit.additional_details && benefit.additional_details.premium_amount) || 0;
                  return (
                    <li key={benefit.uuid || idx} style={styles.benefitItem}>
                      <span style={styles.benefitName}>{benefitName}</span>
                      <span style={styles.benefitPremium}>
                        {currencyCode} {numberInCommaFormat(benefitPremium / 100)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {change.removed.length > 0 && (
            <div>
              <div style={{ ...styles.changeType, ...styles.removedBenefit }}>
                ✗ Benefits Removed:
              </div>
              <ul style={styles.benefitList}>
                {change.removed.map((benefit, idx) => {
                  const benefitName = benefit.name || benefit.type || 'Unknown Benefit';
                  const benefitPremium = benefit.premium_amount ||
                    (benefit.additional_details && benefit.additional_details.premium_amount) || 0;
                  return (
                    <li key={benefit.uuid || idx} style={styles.benefitItem}>
                      <span style={{ ...styles.benefitName, ...styles.removedBenefit }}>
                        {benefitName}
                      </span>
                      <span style={{ ...styles.benefitPremium, ...styles.removedBenefit }}>
                        {currencyCode} {numberInCommaFormat(benefitPremium / 100)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      ))}

      <div style={styles.changeSection}>
        <div style={styles.memberName}>Total Policy Premium</div>
        <div style={styles.premiumSummary}>
          <div style={styles.premiumRow}>
            <span>Current Total:</span>
            <span>{currencyCode} {numberInCommaFormat(originalTotalPremium / 100)}</span>
          </div>
          <div style={styles.premiumRow}>
            <span>New Total:</span>
            <span>{currencyCode} {numberInCommaFormat(newTotalPremium / 100)}</span>
          </div>
          <div style={{ ...styles.premiumRow, ...styles.premiumTotal }}>
            <span>Total Change:</span>
            <span style={{ color: premiumDifference > 0 ? '#28a745' : '#dc3545' }}>
              {premiumDifference > 0 ? '+' : ''}
              {currencyCode} {numberInCommaFormat(premiumDifference / 100)}
            </span>
          </div>
        </div>
      </div>

      <div style={styles.navigationButtons}>
        <ButtonInc onClick={onBack} variant="outlined">
          Back
        </ButtonInc>
        <ButtonInc onClick={onConfirm}>
          Confirm Changes
        </ButtonInc>
      </div>
    </div>
  );
}
